import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Badge,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  EventBusy as EventBusyIcon,
  Person as PersonIcon,
  CalendarToday as CalendarTodayIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase/config';
import {
  collection,
  query,
  where,
  getDocs,
  Timestamp
} from 'firebase/firestore';

const AbsenceCalendar = () => {
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [absences, setAbsences] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateAbsences, setSelectedDateAbsences] = useState([]);
  const [openDayDialog, setOpenDayDialog] = useState(false);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const months = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const daysOfWeek = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  useEffect(() => {
    if (currentUser && userData) {
      fetchAbsences();
    }
  }, [currentUser, userData, currentDate]);

  const fetchAbsences = async () => {
    setLoading(true);
    try {
      // Récupérer tous les chefs de service de l'établissement
      const [snap1, snap2] = await Promise.all([
        getDocs(query(
          collection(db, 'users'),
          where('establishmentId', '==', userData.establishmentId),
          where('role', '==', 'chefService')
        )),
        getDocs(query(
          collection(db, 'users'),
          where('establishmentId', '==', userData.establishmentId),
          where('role', '==', 'chef_service')
        )),
      ]);
      const chefIds = [...new Set([
        ...snap1.docs.map(d => d.id),
        ...snap2.docs.map(d => d.id),
      ])];

      let allAbsences = [];

      if (chefIds.length > 0) {
        // Récupérer les absences par chefServiceId
        const results = await Promise.all(
          chefIds.map(chefId =>
            getDocs(query(
              collection(db, 'absences'),
              where('chefServiceId', '==', chefId)
            ))
          )
        );
        results.forEach(snap => {
          snap.docs.forEach(d => allAbsences.push({ id: d.id, ...d.data() }));
        });
      }

      // Fallback : query directe par establishmentId si les absences ont ce champ
      if (allAbsences.length === 0) {
        const snap = await getDocs(query(
          collection(db, 'absences'),
          where('establishmentId', '==', userData.establishmentId)
        ));
        snap.docs.forEach(d => allAbsences.push({ id: d.id, ...d.data() }));
      }

      // Filtrer sur le mois affiché
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth   = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59);

      allAbsences = allAbsences.filter(absence => {
        const start = toDay(absence.dateDebut);
        const end   = toDay(absence.dateFin);
        return start <= endOfMonth && end >= startOfMonth;
      });

      setAbsences(allAbsences);
    } catch (error) {
      console.error('Erreur lors de la récupération des absences:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay() === 0 ? 7 : firstDay.getDay(); // Lundi = 1

    const days = [];
    
    // Ajouter les jours vides du début
    for (let i = 1; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Ajouter tous les jours du mois
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const toDay = (value) => {
    const d = value?.toDate ? value.toDate() : new Date(value);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  const getAbsencesForDate = (date) => {
    if (!date) return [];
    const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    return absences.filter(absence => {
      const startDay = toDay(absence.dateDebut);
      const endDay   = toDay(absence.dateFin);
      return dayStart >= startDay && dayStart <= endDay;
    }).filter(absence => {
      if (filter === 'all') return true;
      return absence.statut === filter;
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'en attente': return '#ff9800';
      case 'validé':     return '#4caf50';
      case 'refusé':     return '#f44336';
      default:           return '#757575';
    }
  };

  const handleDateClick = (date) => {
    if (!date) return;
    
    const dayAbsences = getAbsencesForDate(date);
    setSelectedDate(date);
    setSelectedDateAbsences(dayAbsences);
    setOpenDayDialog(true);
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const days = getDaysInMonth(currentDate);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* En-tête */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/absences')}
        >
          Retour aux absences
        </Button>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarTodayIcon color="primary" />
          Calendrier des Absences
        </Typography>
      </Box>

      {/* Contrôles du calendrier */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigateMonth(-1)}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="h5" sx={{ minWidth: 200, textAlign: 'center' }}>
              {months[currentDate.getMonth()]} {currentDate.getFullYear()}
            </Typography>
            <IconButton onClick={() => navigateMonth(1)}>
              <ChevronRightIcon />
            </IconButton>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<TodayIcon />}
              onClick={goToToday}
            >
              Aujourd'hui
            </Button>
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Filtre</InputLabel>
              <Select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                startAdornment={<FilterListIcon sx={{ mr: 1 }} />}
              >
                <MenuItem value="all">Toutes</MenuItem>
                <MenuItem value="en attente">En attente</MenuItem>
                <MenuItem value="validé">Validées</MenuItem>
                <MenuItem value="refusé">Refusées</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Légende */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Chip size="small" label="En attente" sx={{ bgcolor: '#ff9800', color: 'white' }} />
          <Chip size="small" label="Validée"    sx={{ bgcolor: '#4caf50', color: 'white' }} />
          <Chip size="small" label="Refusée"    sx={{ bgcolor: '#f44336', color: 'white' }} />
        </Box>
      </Paper>

      {/* Calendrier */}
      <Paper sx={{ p: 2 }}>
        {/* En-têtes des jours */}
        <Grid container spacing={1} sx={{ mb: 1 }}>
          {daysOfWeek.map((day) => (
            <Grid item xs key={day} sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
                {day}
              </Typography>
            </Grid>
          ))}
        </Grid>

        {/* Grille du calendrier */}
        <Grid container spacing={1}>
          {days.map((date, index) => {
            const dayAbsences = date ? getAbsencesForDate(date) : [];
            const isToday = date && date.toDateString() === new Date().toDateString();
            const hasAbsences = dayAbsences.length > 0;

            return (
              <Grid item xs key={index}>
                <Card
                  sx={{
                    minHeight: 100,
                    cursor: date ? 'pointer' : 'default',
                    bgcolor: date ? (isToday ? 'primary.light' : 'background.paper') : 'grey.100',
                    border: isToday ? 2 : 1,
                    borderColor: isToday ? 'primary.main' : 'divider',
                    '&:hover': date ? {
                      bgcolor: 'action.hover',
                      transform: 'scale(1.02)',
                      transition: 'all 0.2s'
                    } : {}
                  }}
                  onClick={() => handleDateClick(date)}
                >
                  <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                    {date && (
                      <>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: isToday ? 'bold' : 'normal',
                            color: isToday ? 'primary.contrastText' : 'text.primary'
                          }}
                        >
                          {date.getDate()}
                        </Typography>
                        
                        {hasAbsences && (
                          <Box sx={{ mt: 0.5 }}>
                            <Badge 
                              badgeContent={dayAbsences.length} 
                              color="error"
                              sx={{ width: '100%' }}
                            >
                              <Box sx={{ width: '100%' }}>
                                {dayAbsences.slice(0, 2).map((absence, idx) => (
                                  <Box
                                    key={idx}
                                    sx={{
                                      height: 4,
                                      bgcolor: getStatusColor(absence.statut),
                                      borderRadius: 1,
                                      mb: 0.5,
                                      opacity: 0.8
                                    }}
                                  />
                                ))}
                                {dayAbsences.length > 2 && (
                                  <Typography variant="caption" color="text.secondary">
                                    +{dayAbsences.length - 2} autres
                                  </Typography>
                                )}
                              </Box>
                            </Badge>
                          </Box>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* Dialog des détails du jour */}
      <Dialog 
        open={openDayDialog} 
        onClose={() => setOpenDayDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventBusyIcon />
          Absences du {selectedDate && formatDate(selectedDate)}
        </DialogTitle>
        <DialogContent>
          {selectedDateAbsences.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <EventBusyIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Aucune absence ce jour
              </Typography>
            </Box>
          ) : (
            <List>
              {selectedDateAbsences.map((absence) => (
                <ListItem key={absence.id} divider>
                  <ListItemIcon>
                    <PersonIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1">
                          {absence.nomPersonnel} {absence.prenomPersonnel}
                        </Typography>
                        <Chip 
                          label={absence.statut}
                          size="small"
                          sx={{ 
                            bgcolor: getStatusColor(absence.statut),
                            color: 'white'
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Type:</strong> {absence.type}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Période:</strong> {' '}
                          {(absence.dateDebut?.toDate() || new Date(absence.dateDebut)).toLocaleDateString('fr-FR')} - {' '}
                          {(absence.dateFin?.toDate() || new Date(absence.dateFin)).toLocaleDateString('fr-FR')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Motif:</strong> {absence.motif}
                        </Typography>
                        {absence.validationComment && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Commentaire:</strong> {absence.validationComment}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDayDialog(false)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AbsenceCalendar;
